import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Input } from '../components/ui/input.js';
import { Button } from '../components/ui/button.js';
import { Icon, type IconName } from '../components/ui/icon.js';
import { GameCard, GameRow, type GameLike } from '../components/lobby/GameCard.js';
import { HeroCarousel } from '../components/lobby/HeroCarousel.js';
import { RecentlyPlayed } from '../components/lobby/RecentlyPlayed.js';
import { ProviderLogoRow } from '../components/lobby/ProviderLogoRow.js';
import { SlotProviderGrid } from '../components/lobby/SlotProviderGrid.js';
import { ProviderGrid } from '../components/lobby/ProviderGrid.js';
import { CasinoLiveBets } from '../components/lobby/CasinoLiveBets.js';
import { EmptyState } from '../components/lobby/EmptyState.js';
import { GamesGridSkeleton } from '../components/lobby/GamesGridSkeleton.js';
import { SiteFooter } from '../components/site/SiteFooter.js';
import { useGameLaunch } from '../features/games/useGameLaunch.js';
import { useAuthStore } from '../stores/auth.js';
import {
  fetchProviders,
  fetchAllGames,
  GAMES_QUERY_KEYS,
  type Game,
  type GameProvider,
} from '../features/games/api.js';
import { sortLotteryFirst } from '../features/games/lotterySort.js';
import { ORIGINALS } from '../features/originals/catalog.js';
import { originalName } from '../features/originals/names.js';
import { useOriginalsCatalog, withOriginalsCatalog, withOriginalsRowArt } from '../features/originals/useOriginalsCatalog.js';

// Originals row is driven by the canonical ORIGINALS list (the same source as
// the home wheel + navbar dropdown) so all three stay in sync — not the catalog,
// which only carries a subset as launchable entries.
const ORIGINALS_CARDS: GameLike[] = ORIGINALS.map((o) => ({
  id: `originals:${o.code}`,
  name: o.name,
  code: o.code,
  gameType: o.code,
  providerDisplayName: 'Originals',
}));

const ORIGINAL_CODES = new Set(ORIGINALS.map((o) => o.code));

/** A catalog row for one of the in-house Originals (the catalog carries a few of them). */
function isOriginalsGame(g: Game): boolean {
  return g.providerCode === 'originals' && ORIGINAL_CODES.has(g.code);
}

const casinoSearchSchema = z.object({
  gameType: z.string().optional(),
  category: z.string().optional(),
  providerId: z.string().optional(),
  view: z.literal('providers').optional(),
});

// Public — no auth guard. Unauth users browse the catalog and only hit the
// login redirect when they click "Play" on a specific game.
export const Route = createFileRoute('/_player/casino/')({
  validateSearch: casinoSearchSchema,
  component: CasinoPage,
});

// ── Buckets ───────────────────────────────────────────────────────────────────
// A game's bucket is its category when it's lottery/sport (those cross gameType),
// else its gameType. Tabs + lobby rows are built from whatever buckets the fetched
// catalog actually contains, so a new type surfaces without touching this file.
const BUCKET_ORDER = ['slot', 'live', 'fish', 'table', 'card', 'crash', 'lottery', 'sport', 'other'] as const;
type Bucket = (typeof BUCKET_ORDER)[number];

const BUCKET_ICON: Record<string, IconName> = {
  slot: 'cherry', live: 'video', fish: 'fish', table: 'spade',
  card: 'spade', crash: 'trendUp', lottery: 'ticket', sport: 'trendUp', other: 'star',
};

function bucketOf(g: Game): Bucket {
  if (g.category === 'lottery') return 'lottery';
  if (g.category === 'sport') return 'sport';
  const gt = g.gameType as Bucket;
  return (BUCKET_ORDER as readonly string[]).includes(gt) ? gt : 'other';
}

function tabFromSearch(s: z.infer<typeof casinoSearchSchema>): string {
  if (s.category === 'lottery' || s.category === 'sport') return s.category;
  if (s.gameType && (BUCKET_ORDER as readonly string[]).includes(s.gameType)) return s.gameType;
  return 'lobby';
}

function searchForTab(key: string): z.infer<typeof casinoSearchSchema> {
  if (key === 'lobby') return {};
  if (key === 'lottery' || key === 'sport') return { category: key };
  return { gameType: key };
}

function CasinoPage() {
  const { t, i18n } = useTranslation(['casino', 'common']);
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const search = Route.useSearch();

  // Tab + provider live in the URL so back (browser or phone) walks
  // provider games → provider cards → tab, and the home page can deep-link a camp.
  const tab = tabFromSearch(search);
  const provId = search.providerId ?? 'all';
  const [q, setQ] = useState('');
  const [launchingId, setLaunchingId] = useState<string | null>(null);
  const { launch, modal: launchModal } = useGameLaunch({ lang: i18n.language ?? 'th' });

  // The lobby is tall (hero + many rows) and the filtered grids are short, but
  // they all render on this one route — so a view swap keeps the document
  // scroll offset and drops the player partway down content they never
  // scrolled to. TanStack Router has no scroll restoration here, so reset it by
  // hand whenever the view swaps (row "see all", provider logos and cards, back).
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [tab, provId]);

  async function onPlay(game: Game) {
    if (!accessToken) {
      // An Original listed in the catalog opens its in-house page, which a
      // visitor may see; any other game needs a session to launch.
      if (isOriginalsGame(game)) {
        void navigate({ to: '/games/originals/$code', params: { code: game.code } });
        return;
      }
      void navigate({ to: '/login' });
      return;
    }
    // Lottery + sportsbook keep their dedicated pages: lottery deep-links to its
    // round view, sport hands off to the in-frame sportsbook launcher on /sports.
    const b = bucketOf(game);
    if (b === 'lottery') {
      void navigate({ to: '/lottery/$code', params: { code: game.code } });
      return;
    }
    if (b === 'sport') {
      void navigate({ to: '/sports' });
      return;
    }
    // Popup/redirect providers must launch inside the click's user gesture —
    // routing first would get the popup blocked. Everything else goes through
    // the deep-linkable /casino/$code route (the iframe mounts in-page there).
    if (game.launchMode === 'popup' || game.launchMode === 'redirect') {
      setLaunchingId(game.id);
      try {
        await launch(game);
      } finally {
        setLaunchingId(null);
      }
      return;
    }
    void navigate({ to: '/casino/$code', params: { code: game.code } });
  }

  const providersQuery = useQuery({
    queryKey: GAMES_QUERY_KEYS.providers,
    queryFn: fetchProviders,
    staleTime: 60_000,
  });

  // Fetch the whole catalog once (paging past the backend's 200/page clamp); the
  // lobby rows + the filtered grid both derive from this single set so we never
  // fire a query per row. Lottery rounds rotate ~15 min, so refetch in the
  // foreground to keep countdowns/state fresh.
  const gamesQuery = useQuery({
    queryKey: [...GAMES_QUERY_KEYS.list({}), 'casino-all'],
    queryFn: () => fetchAllGames(),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  // Originals cards with localized display names (the catalog name is the English
  // fallback), plus the catalog's RTP and any art uploaded in BO → Games.
  const originalsCatalog = useOriginalsCatalog();
  const originalsCards = useMemo(
    () =>
      withOriginalsCatalog(
        ORIGINALS_CARDS.map((g) => ({ ...g, name: originalName(g.code ?? '', t) })),
        originalsCatalog,
      ),
    [t, originalsCatalog],
  );
  const all = useMemo<Game[]>(() => withOriginalsRowArt(gamesQuery.data ?? []), [gamesQuery.data]);
  const providers = useMemo<GameProvider[]>(() => providersQuery.data?.items ?? [], [providersQuery.data]);
  const selectedProvider = providers.find((p) => p.id === provId);
  const providerName = selectedProvider?.displayName ?? '';
  // The in-house Originals "provider" maps to the canonical 7-game list, not the
  // 3 catalog entries that happen to carry its providerId.
  const isOriginalsProvider =
    selectedProvider != null &&
    (selectedProvider.launchMode === 'internal' ||
      selectedProvider.code === 'originals' ||
      selectedProvider.displayName === 'Originals');

  // Tab/row label — localized when a key exists, else a capitalized fallback so a
  // rare gameType never renders a raw `tabs.foo` string.
  const tabLabel = (key: string) =>
    t(`tabs.${key}`, { defaultValue: key.charAt(0).toUpperCase() + key.slice(1) });

  // Buckets actually present in the catalog, in canonical order.
  const presentBuckets = useMemo(() => {
    const s = new Set(all.map(bucketOf));
    return BUCKET_ORDER.filter((b) => s.has(b));
  }, [all]);

  const tabs = useMemo(
    () => [{ key: 'lobby', icon: 'grid' as IconName }, ...presentBuckets.map((b) => ({ key: b, icon: BUCKET_ICON[b] ?? 'grid' }))],
    [presentBuckets],
  );

  // Whether to show a flat grid (a tab/provider/search is active) vs the lobby rows.
  const viewAllProviders = search.view === 'providers' && provId === 'all' && !q.trim();
  const showGrid = tab !== 'lobby' || provId !== 'all' || q.trim() !== '' || viewAllProviders;
  // Slots is browsed camp-first: provider cards, then one camp's slot games,
  // fetched on demand rather than sliced from the catalog above.
  const showSlotProviders = tab === 'slot' && provId === 'all' && !q.trim();
  const showSlotProviderGames = tab === 'slot' && provId !== 'all';

  const providerGamesQuery = useQuery({
    queryKey: [...GAMES_QUERY_KEYS.list({ providerId: provId, gameType: 'slot' }), 'all'],
    queryFn: () => fetchAllGames({ providerId: provId, gameType: 'slot' }),
    enabled: showSlotProviderGames,
    staleTime: 30_000,
  });

  const gridGames = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let items = showSlotProviderGames ? (providerGamesQuery.data ?? []) : all;
    if (!showSlotProviderGames && tab !== 'lobby') items = items.filter((g) => bucketOf(g) === tab);
    if (!showSlotProviderGames && provId !== 'all') items = items.filter((g) => g.providerId === provId);
    if (needle) items = items.filter((g) => g.name.toLowerCase().includes(needle));
    return sortLotteryFirst(items);
  }, [all, tab, provId, q, showSlotProviderGames, providerGamesQuery.data]);

  // Featured hero: hot/new games only; hidden when the catalog carries no tags.
  const featured = useMemo(() => all.filter((g) => g.tag === 'hot' || g.tag === 'new').slice(0, 8), [all]);

  // Lobby: one row per present casino bucket (lottery/sport get their own rows below).
  const lobbyRows = useMemo(
    () =>
      presentBuckets
        .filter((b) => b !== 'lottery' && b !== 'sport')
        .map((b) => ({ key: b, icon: BUCKET_ICON[b] ?? 'grid', games: all.filter((g) => bucketOf(g) === b) })),
    [all, presentBuckets],
  );
  const lotteryGames = useMemo(() => sortLotteryFirst(all.filter((g) => bucketOf(g) === 'lottery')), [all]);
  const sportGames = useMemo(() => all.filter((g) => bucketOf(g) === 'sport'), [all]);

  // Open for a visitor too: the game page renders without a session and asks
  // for a sign-in only at the bet.
  function onOriginalPlay(code: string) {
    void navigate({ to: '/games/originals/$code', params: { code } });
  }

  function selectTab(key: string) {
    setQ('');
    void navigate({ to: '/casino', search: searchForTab(key) });
  }
  function selectProvider(id: string) {
    void navigate({ to: '/casino', search: { providerId: id } });
  }

  // Slots stays lit while one of its camps is open; the lobby does not, since a
  // provider logo there filters across every game type.
  const isTabActive = (key: string) => tab === key && (tab !== 'lobby' || provId === 'all') && !q.trim();

  const tabCls = (active: boolean) =>
    [
      'inline-flex items-center gap-2 h-11 px-5 rounded-xl text-[14.5px] font-bold whitespace-nowrap transition-colors',
      active
        ? 'bg-[var(--accent)] text-[var(--accent-on)] shadow-[0_6px_18px_var(--accent-glow)]'
        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--bg-raised)] hover:bg-[var(--bg-elevated)]',
    ].join(' ');

  return (
    <div className="flex flex-col gap-8">
      {launchModal}

      {/* Tab bar + search — inline, blends with the page (no floating panel) */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="flex gap-2 overflow-x-auto bf-noscroll -mx-1 px-1">
            {tabs.map((tb) => (
              <button key={tb.key} type="button" onClick={() => selectTab(tb.key)} aria-pressed={isTabActive(tb.key)} className={tabCls(isTabActive(tb.key))}>
                <Icon name={tb.icon} size={16} />
                {tabLabel(tb.key)}
              </button>
            ))}
          </div>
          <div className="lg:ml-auto lg:w-80">
            <Input
              prefix={<Icon name="search" size={16} className="text-[var(--text-muted)]" />}
              placeholder={t('search.placeholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
      </div>

      {gamesQuery.isPending ? (
        <GamesGridSkeleton count={10} />
      ) : gamesQuery.isError ? (
        <EmptyState
          icon="rotate"
          title={t('loadError.title')}
          body={t('loadError.body')}
          action={
            <Button size="sm" variant="secondary" onClick={() => void gamesQuery.refetch()}>
              {t('common:common.retry')}
            </Button>
          }
        />
      ) : showGrid ? (
        // ── Filtered grid ──
        <section className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">
              {q.trim() ? t('search.results') : viewAllProviders ? t('rows.providers') : provId !== 'all' ? providerName || gridGames[0]?.providerDisplayName : tabLabel(tab)}
              {showSlotProviderGames && !q.trim() && providerGamesQuery.data && (
                <span className="ml-2 text-base md:text-lg font-semibold text-[var(--text-secondary)]">
                  ({providerGamesQuery.data.length})
                </span>
              )}
            </h1>
            <button type="button" onClick={() => selectTab(showSlotProviderGames ? 'slot' : 'lobby')} className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
              <Icon name="arrowRight" size={15} className="rotate-180" />
              {t('back')}
            </button>
          </div>
          {(tab === 'lottery' || tab === 'sport') && (
            <button
              onClick={() => void navigate({ to: tab === 'lottery' ? '/lottery' : '/sports' })}
              className="self-start inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--accent-strong)] hover:underline"
            >
              {t('openFullPage')}
              <Icon name="arrowRight" size={14} />
            </button>
          )}
          {viewAllProviders ? (
            <ProviderGrid providers={providers} onSelect={selectProvider} />
          ) : showSlotProviders ? (
            <SlotProviderGrid />
          ) : isOriginalsProvider ? (
            <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
              {originalsCards.map((g) => (
                <GameCard key={g.id} game={g} onPlay={() => onOriginalPlay(g.code ?? '')} />
              ))}
            </div>
          ) : showSlotProviderGames && providerGamesQuery.isPending ? (
            <GamesGridSkeleton count={10} />
          ) : showSlotProviderGames && providerGamesQuery.isError ? (
            <EmptyState
              icon="rotate"
              title={t('loadError.title')}
              body={t('loadError.body')}
              action={
                <Button size="sm" variant="secondary" onClick={() => void providerGamesQuery.refetch()}>
                  {t('common:common.retry')}
                </Button>
              }
            />
          ) : gridGames.length === 0 ? (
            <EmptyState icon="search" title={t('empty.title')} body={t('empty.body')} />
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
              {gridGames.map((g) => (
                <GameCard key={g.id} game={g} launching={launchingId === g.id} onPlay={() => void onPlay(g)} />
              ))}
            </div>
          )}
        </section>
      ) : (
        // ── Lobby: featured hero + continue-playing + stacked category rows ──
        <>
          {featured.length > 0 && (
            <HeroCarousel games={featured} onPlay={(g) => void onPlay(g as Game)} />
          )}
          {accessToken && <RecentlyPlayed />}
          <GameRow
            title={t('rows.originals')}
            icon="star"
            games={originalsCards}
            onPlay={(g) => onOriginalPlay(g.code ?? '')}
            onAll={() => void navigate({ to: '/games/originals' })}
          />
          {lobbyRows.map((r) => (
            <GameRow
              key={r.key}
              title={tabLabel(r.key)}
              icon={r.icon}
              games={r.games}
              onPlay={(g) => void onPlay(g as Game)}
              onAll={() => selectTab(r.key)}
            />
          ))}
          {lotteryGames.length > 0 && (
            <GameRow
              title={tabLabel('lottery')}
              icon="ticket"
              games={lotteryGames.slice(0, 12)}
              onPlay={(g) => void onPlay(g as Game)}
              onAll={() => void navigate({ to: '/lottery' })}
            />
          )}
          {sportGames.length > 0 && (
            <GameRow
              title={tabLabel('sport')}
              icon="trendUp"
              games={sportGames}
              onPlay={(g) => void onPlay(g as Game)}
              onAll={() => void navigate({ to: '/sports' })}
            />
          )}
          <ProviderLogoRow title={t('rows.providers')} providers={providers} onSelect={selectProvider} onAll={() => void navigate({ to: '/casino', search: { view: 'providers' } })} />
        </>
      )}

      {/* Live bets (real Originals feed) */}
      <CasinoLiveBets />

      {/* Leaderboard / prizepool: pending backend (no real points/prize system yet). */}

      <SiteFooter />
    </div>
  );
}
